// src/app/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import '../styles/index.css';
import App from '../App';
import BoletaCanonicalRenderPage from '../components/acciones/BoletaCanonicalRenderPage';

// ============================================
// FIX #2: Error Boundary Global
// Captura errores de renderizado en toda la aplicación
// ============================================
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Generar un ID de error para referencia del usuario
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, errorId };
  }

  componentDidCatch(error, errorInfo) {
    // FIX #6: Reportar error a servicio de monitoreo
    // En producción, enviar a un servicio como Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // Ejemplo con endpoint propio:
      this.reportError(error, errorInfo);
    } else {
      // En desarrollo, mostrar en consola
      // eslint-disable-next-line no-console
      console.error('Error capturado por GlobalErrorBoundary:', error, errorInfo);
    }
  }

  reportError(error, errorInfo) {
    try {
      const errorReport = {
        message: error?.message || 'Unknown error',
        stack: error?.stack?.substring(0, 1000), // Limitar tamaño
        componentStack: errorInfo?.componentStack?.substring(0, 1000),
        errorId: this.state.errorId,
        timestamp: new Date().toISOString(),
        url: window.location.pathname, // No incluir query params (podrían tener tokens)
        userAgent: navigator.userAgent?.substring(0, 200),
      };

      // Enviar a endpoint de error reporting
      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        fetch(`${apiUrl}/api/client-errors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(errorReport),
        }).catch(() => {
          // Silenciar errores del reporte — no queremos un loop infinito
        });
      }
    } catch {
      // Fallar silenciosamente
    }
  }

  handleReload = () => {
    // Limpiar cualquier estado corrupto antes de recargar
    try {
      sessionStorage.clear();
      // NO limpiar localStorage — el usuario no debería perder su sesión por un error de UI
    } catch {
      // Ignorar
    }
    window.location.reload();
  };

  handleGoHome = () => {
    try {
      sessionStorage.clear();
    } catch {
      // Ignorar
    }
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // FIX #2: UI de error segura — sin exponer detalles técnicos
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            fontFamily:
              "Manrope, 'Segoe UI', system-ui, -apple-system, Arial, sans-serif",
            backgroundColor: '#F8FAFC',
            color: '#1A2E44',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              background: '#FFFFFF',
              borderRadius: '12px',
              padding: '40px 30px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              border: '1px solid #E5E9F2',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h1
              style={{
                fontSize: '22px',
                fontWeight: '700',
                marginBottom: '12px',
                color: '#1A2E44',
              }}
            >
              Algo salió mal
            </h1>
            <p
              style={{
                fontSize: '14px',
                color: '#5a789c',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              Ocurrió un error inesperado. Nuestro equipo ha sido notificado.
              {this.state.errorId && (
                <>
                  <br />
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      fontFamily: "'Courier New', monospace",
                    }}
                  >
                    Referencia: {this.state.errorId}
                  </span>
                </>
              )}
            </p>

            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={this.handleReload}
                style={{
                  background: '#0052FF',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Reintentar
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  background: 'transparent',
                  color: '#0052FF',
                  border: '1px solid #0052FF',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Ir al inicio
              </button>
            </div>

            <p
              style={{
                marginTop: '20px',
                fontSize: '12px',
                color: '#94a3b8',
              }}
            >
              Si el problema persiste, contacta a{' '}
              <a
                href="mailto:soporte@wisby.legal"
                style={{ color: '#0052FF', textDecoration: 'none' }}
              >
                soporte@wisby.legal
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================
// FIX #5: Protección contra framing (clickjacking)
// ============================================
function detectFraming() {
  try {
    if (window.self !== window.top) {
      // La aplicación está embebida en un iframe
      // Verificar si es un origen permitido
      // En la mayoría de los casos, Wisby no debería estar en un iframe
      if (process.env.NODE_ENV === 'production') {
        // eslint-disable-next-line no-console
        console.warn('Aplicación detectada dentro de un iframe');
        // Intentar romper el frame
        window.top.location = window.self.location;
      }
    }
  } catch {
    // Si hay error accediendo a window.top, estamos en un iframe cross-origin
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error('Aplicación embebida en iframe cross-origin no autorizado');
      // Mostrar advertencia en lugar de la app
      document.body.innerHTML =
        '<div style="text-align:center;padding:50px;font-family:sans-serif;">' +
        '<h1>⚠️ Acceso no autorizado</h1>' +
        '<p>Esta aplicación no puede ejecutarse dentro de un marco externo.</p>' +
        '<p>Visita <a href="https://wisby.legal">wisby.legal</a> directamente.</p>' +
        '</div>';
    }
  }
}

// ============================================
// FIX #6: Global error handlers para errores no capturados
// ============================================
function setupGlobalErrorHandlers() {
  // Capturar errores de JavaScript no manejados
  window.addEventListener('error', (event) => {
    if (process.env.NODE_ENV === 'production') {
      // No mostrar detalles técnicos al usuario
      // El Error Boundary de React manejará errores de renderizado
      // Esto captura errores fuera del árbol de React

      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        try {
          fetch(`${apiUrl}/api/client-errors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'uncaught_error',
              message: event.message?.substring(0, 500),
              filename: event.filename?.substring(0, 200),
              lineno: event.lineno,
              colno: event.colno,
              timestamp: new Date().toISOString(),
              url: window.location.pathname,
            }),
          }).catch(() => {});
        } catch {
          // Silenciar
        }
      }
    }
  });

  // Capturar promise rejections no manejadas
  window.addEventListener('unhandledrejection', (event) => {
    if (process.env.NODE_ENV === 'production') {
      // Prevenir que el error se muestre en la consola del usuario
      event.preventDefault();

      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        try {
          fetch(`${apiUrl}/api/client-errors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'unhandled_rejection',
              message:
                event.reason instanceof Error
                  ? event.reason.message?.substring(0, 500)
                  : String(event.reason)?.substring(0, 500),
              timestamp: new Date().toISOString(),
              url: window.location.pathname,
            }),
          }).catch(() => {});
        } catch {
          // Silenciar
        }
      }
    }
  });
}

// ============================================
// INICIALIZACIÓN
// ============================================

// FIX #5: Detectar framing antes de renderizar
detectFraming();

// FIX #6: Configurar handlers globales de errores
setupGlobalErrorHandlers();

// FIX #3: Verificar que el elemento root existe
const rootElement = document.getElementById('root');

if (!rootElement) {
  // eslint-disable-next-line no-console
  console.error(
    'Elemento root no encontrado. La aplicación no puede inicializarse.'
  );

  // Intentar crear un mensaje visible para el usuario
  document.body.innerHTML =
    '<div style="text-align:center;padding:50px;font-family:sans-serif;">' +
    '<h1>Error de inicialización</h1>' +
    '<p>No se pudo cargar la aplicación. Intenta recargar la página.</p>' +
    '<button onclick="window.location.reload()" ' +
    'style="padding:12px 24px;background:#0052FF;color:white;border:none;' +
    'border-radius:8px;cursor:pointer;font-size:14px;">Recargar</button>' +
    '</div>';
} else {
  const root = ReactDOM.createRoot(rootElement);
  const isCanonicalBoletaRender = window.location.pathname === '/internal/boleta-render';

  // FIX #1: React.StrictMode HABILITADO
  // StrictMode es esencial para detectar:
  // - Side effects inseguros en constructores
  // - APIs legacy deprecadas
  // - findDOMNode deprecado
  // - Context API legacy
  // - Refs inseguros (string refs)
  // El "double render" en desarrollo es INTENCIONAL y ayuda a detectar bugs
  root.render(
    <React.StrictMode>
      <GlobalErrorBoundary>
        {isCanonicalBoletaRender ? <BoletaCanonicalRenderPage /> : <App />}
      </GlobalErrorBoundary>
    </React.StrictMode>
  );
}
